import { useState, useRef, useEffect } from "react";
import { Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { VoiceButton } from "./ExpenseInput/VoiceButton";
import { SaveFeedback } from "./ExpenseInput/SaveFeedback";
import { SuccessCard } from "./ExpenseInput/SuccessCard";
import { QuickActions } from "./ExpenseInput/QuickActions";
import { ConfirmRow } from "./ExpenseInput/ConfirmRow";
import { TypeToggle } from "./ExpenseInput/TypeToggle";
import { useHapticFeedback } from "@/hooks/useHapticFeedback";
import { trackEvent } from "@/lib/analytics";
import { useOfflineQueue } from "@/hooks/useOfflineQueue";
import { awardXP, updateStreak, XP_REWARDS, getUserProgress } from "@/lib/gamification";
import { checkAndAwardBadgesForEvent } from "@/lib/badges";
import { expenseSchema } from "@/lib/expenseValidation";
import { ZodError } from "zod";
import { useGamification } from "@/contexts/GamificationContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { cn } from "@/lib/utils";
import { useSessionDraft } from "@/hooks/useSessionDraft";

interface ParsedExpense {
  amount: number;
  currency: string;
  merchant: string;
  category: string;
  note: string;
  confidence: number;
  date: string;
  is_clarifying: boolean;
  question?: string;
  suggestions?: string[];
  type?: 'expense' | 'income';
  income_source?: string;
  income_category?: string;
  tag?: string | null;
  suggested_tag?: string | null;
}

interface ExpenseInputProps {
  onExpenseAdded: () => void;
  pushUndo: (action: any) => void;
  onBadgeUnlocked?: (badgeIds: string[]) => void;
}

const COMMON_CATEGORIES = ['Food', 'Transport', 'Shopping', 'Fun', 'Health', 'Housing', 'Bills/Other'];

export const ExpenseInput = ({ onExpenseAdded, pushUndo, onBadgeUnlocked }: ExpenseInputProps) => {
  const [input, setInput, clearInputDraft] = useSessionDraft<string>('cashee:draft:manual-input', '');
  const [isParsing, setIsParsing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [parsedData, setParsedData, clearParsedDraft] = useSessionDraft<ParsedExpense | null>('cashee:draft:parsed-data', null);
  const [showSaveFeedback, setShowSaveFeedback] = useState(false);
  const [showSuccessCard, setShowSuccessCard] = useState(false);
  const [savedExpense, setSavedExpense] = useState<any>(null);
  const [savedIncome, setSavedIncome] = useState<any>(null);
  const [streakDays, setStreakDays] = useState<number>(0);
  const [user, setUser] = useState<any>(null);
  const [startTime, setStartTime] = useState<number>(0);
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [userCurrency, setUserCurrency] = useState('EUR');
  const [isVoiceRecording, setIsVoiceRecording] = useState(false);
  const { toast } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const { success } = useHapticFeedback();
  const { enqueue, isOnline } = useOfflineQueue();
  const { formatConfirmation, settings } = useGamification();
  // Unified calm mode from context
  const calmMode = settings.hideRewards;
  const { t } = useLanguage();

  const isIncome = parsedData?.type === 'income';

  useEffect(() => {
    // Get user and load currency
    const loadUserData = async () => {
      const { data } = await supabase.auth.getUser();
      if (data?.user) {
        setUser(data.user);
        
        // Load user's currency
        const { data: budgetData } = await supabase
          .from('budget_settings')
          .select('currency')
          .eq('user_id', data.user.id)
          .maybeSingle();
        
        if (budgetData?.currency) {
          setUserCurrency(budgetData.currency);
        }
      }
    };
    loadUserData();

    // Scroll input into view when keyboard opens on mobile
    const handleResize = () => {
      if (inputRef.current && document.activeElement === inputRef.current) {
        setTimeout(() => {
          inputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleParse = async () => {
    if (!input.trim()) return;

    setStartTime(Date.now());
    trackEvent({ name: 'log_start', props: { source: 'text' } });
    
    // Natural language AI parsing
    setIsParsing(true);
    try {
      const { data, error } = await supabase.functions.invoke('parse-expense', {
        body: { 
          text: input,
          context: {
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            preferredCurrency: userCurrency
          }
        }
      });

      if (error) throw error;
      
      if (data.success && data.items?.length > 0) {
        const parsed = data.items[0];
        setParsedData(parsed);
        
        trackEvent({
          name: 'log_parsed',
          props: {
            confidence: parsed.confidence,
            suggested_category: parsed.category
          }
        });
        
        if (parsed.is_clarifying) {
          toast({
            title: t('expense.needClarification'),
            description: parsed.question,
          });
        }
      } else {
        throw new Error(data.error || 'Parsing failed');
      }
    } catch (error) {
      console.error('Parse error:', error);
      toast({
        title: "Parsing failed",
        description: "Please try rephrasing your expense",
        variant: "destructive",
      });
    } finally {
      setIsParsing(false);
    }
  };

  const handleSave = async () => {
    if (!parsedData || parsedData.is_clarifying) return;

    setIsSaving(true);
    const saveStartTime = Date.now();
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Normalize date to YYYY-MM-DD format
      const dateStr = parsedData.date.includes('T') 
        ? parsedData.date.split('T')[0] 
        : parsedData.date;

      // Validate input data
      const validatedData = expenseSchema.parse({
        amount: parsedData.amount,
        merchant: parsedData.merchant,
        category: parsedData.category,
        note: parsedData.note || null,
        expense_date: dateStr,
        currency: parsedData.currency,
        tag: parsedData.tag || null,
      });

      const expenseData = {
        user_id: user.id,
        amount: validatedData.amount,
        currency: validatedData.currency,
        merchant: validatedData.merchant,
        category: validatedData.category,
        note: validatedData.note,
        expense_date: validatedData.expense_date,
        tag: validatedData.tag || null,
      };

      let savedExpenseData;
      
      if (isOnline) {
        const { data, error } = await supabase.from('expenses').insert(expenseData).select().single();
        if (error) throw error;
        savedExpenseData = data;
      } else {
        enqueue('insert', expenseData);
        savedExpenseData = { ...expenseData, id: crypto.randomUUID() };
      }

      // Immediate refresh for Budget screen
      onExpenseAdded();

      const msToSave = Date.now() - saveStartTime;
      trackEvent({
        name: 'log_saved',
        props: { ms_to_save: msToSave, amount: parsedData.amount }
      });

      // Save as template if checkbox was checked
      if (saveAsTemplate && user) {
        const templateName = parsedData.merchant || parsedData.category;
        try {
          await supabase
            .from('expense_templates')
            .insert({
              user_id: user.id,
              name: templateName,
              amount: parsedData.amount,
              merchant: parsedData.merchant,
              category: parsedData.category,
              currency: parsedData.currency,
              note: parsedData.note || null,
            });
          
          toast({
            title: t('expense.templateSaved'),
            description: t('expense.templateDescription', { name: templateName }),
            duration: 2000,
          });
        } catch (error) {
          console.error('Error saving template:', error);
        }
        setSaveAsTemplate(false);
      }

      // Award gamification rewards
      const streakResult = await updateStreak(user.id);
      const updatedProgress = await getUserProgress(user.id);
      setStreakDays(updatedProgress?.current_streak || 0);
      await awardXP(user.id, XP_REWARDS.LOG_EXPENSE, 'expense');

      // Update cached counts for badge criteria
      const isCoffee = [parsedData.merchant, parsedData.note].some(
        s => s && s.toLowerCase().includes('coffee')
      );
      const progressUpdates: Record<string, number> = {};
      if (isCoffee) {
        progressUpdates.coffee_count = (updatedProgress.coffee_count ?? 0) + 1;
        updatedProgress.coffee_count = progressUpdates.coffee_count;
      }

      // Update max_merchant_count
      const { data: merchantCounts } = await supabase
        .from('expenses')
        .select('merchant')
        .eq('user_id', user.id);
      if (merchantCounts) {
        const freq: Record<string, number> = {};
        merchantCounts.forEach(e => { const m = e.merchant.toLowerCase(); freq[m] = (freq[m] || 0) + 1; });
        const maxCount = Math.max(0, ...Object.values(freq));
        if (maxCount > (updatedProgress.max_merchant_count ?? 0)) {
          progressUpdates.max_merchant_count = maxCount;
          updatedProgress.max_merchant_count = maxCount;
        }
      }

      // Update recurring_count
      const { count: recurringCount } = await supabase
        .from('recurring_expenses')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('is_active', true);
      if ((recurringCount ?? 0) > (updatedProgress.recurring_count ?? 0)) {
        progressUpdates.recurring_count = recurringCount ?? 0;
        updatedProgress.recurring_count = recurringCount ?? 0;
      }

      if (Object.keys(progressUpdates).length > 0) {
        await supabase.from('user_progress').update(progressUpdates).eq('user_id', user.id);
      }

      // Re-fetch progress after XP award for accurate badge checks
      const latestProgress = await getUserProgress(user.id);

      // Check badges for expense_logged event
      const foreignCurrency = parsedData.currency !== userCurrency;
      const expenseBadges = await checkAndAwardBadgesForEvent(
        user.id, 'expense_logged', latestProgress,
        { daysSinceLast: streakResult.daysSinceLast, foreignCurrency }
      );

      // Check badges for streak_updated event
      const streakBadges = await checkAndAwardBadgesForEvent(
        user.id, 'streak_updated', latestProgress,
        { previousLongest: streakResult.previousLongest }
      );

      const newBadges = [...new Set([...expenseBadges, ...streakBadges])];
      if (newBadges.length > 0 && onBadgeUnlocked) {
        onBadgeUnlocked(newBadges);
      }

      const graceUsed = streakResult.graceUsed;

      if (graceUsed) {
        toast({
          title: "Streak saved! 💪",
          description: "We used the grace period to keep your momentum going.",
          duration: 3000,
        });
      }

      // Show toast notification with session-based subtext
      const showSubtext = !sessionStorage.getItem('progress_subtext_shown') && 
                          settings.style !== 'minimal' && 
                          !settings.hideRewards;
      
      toast({
        title: formatConfirmation(XP_REWARDS.LOG_EXPENSE),
        description: showSubtext ? "Progress unlocks small milestones. Turn off in Settings." : undefined,
        duration: 2000,
      });

      if (showSubtext) {
        sessionStorage.setItem('progress_subtext_shown', 'true');
      }

      // Save for success card display
      setSavedExpense({
        amount: parsedData.amount,
        currency: parsedData.currency,
        category: parsedData.category,
        merchant: parsedData.merchant,
        note: parsedData.note,
      });

      // Show success card instead of toast
      setShowSuccessCard(true);

      pushUndo({ type: 'save', expense: savedExpenseData });

    } catch (error) {
      console.error('Save error:', error);
      if (error instanceof ZodError) {
        toast({
          title: t('expense.invalidInput'),
          description: error.errors[0].message,
          variant: "destructive",
        });
      } else {
        toast({
          title: t('expense.couldntSave'),
          description: t('expense.maybeOffline'),
          variant: "destructive",
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveIncome = async () => {
    if (!parsedData || !isIncome) return;

    setIsSaving(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const dateStr = parsedData.date.includes('T') 
        ? parsedData.date.split('T')[0] 
        : parsedData.date;

      const incomeData = {
        user_id: user.id,
        amount: parsedData.amount,
        currency: parsedData.currency,
        source: parsedData.income_source || parsedData.merchant || 'Payment',
        category: parsedData.income_category || 'other',
        income_date: dateStr,
        note: parsedData.note || null,
      };

      const { data, error } = await supabase.from('income').insert(incomeData).select().single();
      if (error) throw error;

      setSavedIncome({
        amount: parsedData.amount,
        currency: parsedData.currency,
        source: incomeData.source,
      });

      toast({
        title: t('income.logged') || '💰 Income logged!',
        description: `${parsedData.currency} ${parsedData.amount.toFixed(2)} from ${incomeData.source}`,
        duration: 3000,
      });

      setShowSuccessCard(true);
      onExpenseAdded();

    } catch (error) {
      console.error('Income save error:', error);
      toast({
        title: t('expense.couldntSave'),
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleUndo = async (expenseId: string) => {
    try {
      if (isOnline) {
        await supabase.from('expenses').delete().eq('id', expenseId);
      } else {
        enqueue('delete', { id: expenseId });
      }
      onExpenseAdded();
      trackEvent({ name: 'log_undo', props: { action_type: 'save' } });
    } catch (error) {
      console.error('Undo error:', error);
    }
  };

  const handleQuickAdd = async (expense: { category: string; merchant: string; amount: number; currency: string }) => {
    if (!user) return;

    try {
      const expenseData = {
        user_id: user.id,
        amount: expense.amount,
        merchant: expense.merchant,
        category: expense.category,
        expense_date: new Date().toISOString().split('T')[0],
        currency: expense.currency,
      };

      let savedExpenseData;
      if (isOnline) {
        const { data, error } = await supabase.from('expenses').insert(expenseData).select().single();
        if (error) throw error;
        savedExpenseData = data;
      } else {
        enqueue('insert', expenseData);
        savedExpenseData = { ...expenseData, id: crypto.randomUUID() };
      }

      // Immediate refresh for Budget screen
      onExpenseAdded();

      // Save for success card
      setSavedExpense({
        amount: expense.amount,
        currency: expense.currency,
        category: expense.category,
        merchant: expense.merchant,
      });

      // Gamification
      const streakResult = await updateStreak(user.id);
      const updatedProgress = await getUserProgress(user.id);
      setStreakDays(updatedProgress?.current_streak || 0);
      await awardXP(user.id, XP_REWARDS.LOG_EXPENSE, 'expense');

      // Update cached counts for badge criteria
      const isCoffeeQuick = [expense.merchant].some(
        s => s && s.toLowerCase().includes('coffee')
      );
      const quickUpdates: Record<string, number> = {};
      if (isCoffeeQuick) {
        quickUpdates.coffee_count = (updatedProgress.coffee_count ?? 0) + 1;
        updatedProgress.coffee_count = quickUpdates.coffee_count;
      }
      const { data: merchantCountsQuick } = await supabase
        .from('expenses')
        .select('merchant')
        .eq('user_id', user.id);
      if (merchantCountsQuick) {
        const freq: Record<string, number> = {};
        merchantCountsQuick.forEach(e => { const m = e.merchant.toLowerCase(); freq[m] = (freq[m] || 0) + 1; });
        const maxCount = Math.max(0, ...Object.values(freq));
        if (maxCount > (updatedProgress.max_merchant_count ?? 0)) {
          quickUpdates.max_merchant_count = maxCount;
          updatedProgress.max_merchant_count = maxCount;
        }
      }
      const { count: recurringCountQuick } = await supabase
        .from('recurring_expenses')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('is_active', true);
      if ((recurringCountQuick ?? 0) > (updatedProgress.recurring_count ?? 0)) {
        quickUpdates.recurring_count = recurringCountQuick ?? 0;
        updatedProgress.recurring_count = recurringCountQuick ?? 0;
      }
      if (Object.keys(quickUpdates).length > 0) {
        await supabase.from('user_progress').update(quickUpdates).eq('user_id', user.id);
      }

      const latestProgress = await getUserProgress(user.id);
      const expenseBadges = await checkAndAwardBadgesForEvent(
        user.id, 'expense_logged', latestProgress,
        { daysSinceLast: streakResult.daysSinceLast, foreignCurrency: expense.currency !== userCurrency }
      );
      const streakBadges = await checkAndAwardBadgesForEvent(
        user.id, 'streak_updated', latestProgress,
        { previousLongest: streakResult.previousLongest }
      );
      const newBadges = [...new Set([...expenseBadges, ...streakBadges])];
      if (newBadges.length > 0 && onBadgeUnlocked) {
        onBadgeUnlocked(newBadges);
      }

      const graceUsed = streakResult.graceUsed;

      if (graceUsed) {
        toast({
          title: "Streak saved! 💪",
          description: "We used the grace period to keep your momentum going.",
          duration: 3000,
        });
      }

      // Show toast notification with session-based subtext
      const showSubtext = !sessionStorage.getItem('progress_subtext_shown') && 
                          settings.style !== 'minimal' && 
                          !settings.hideRewards;
      
      toast({
        title: formatConfirmation(XP_REWARDS.LOG_EXPENSE),
        description: showSubtext ? "Progress unlocks small milestones. Turn off in Settings." : undefined,
        duration: 2000,
      });

      if (showSubtext) {
        sessionStorage.setItem('progress_subtext_shown', 'true');
      }

      trackEvent({
        name: 'log_saved',
        props: { ms_to_save: 0, amount: expense.amount }
      });

      // Show success card
      setShowSuccessCard(true);

      pushUndo({ type: 'save', expense: savedExpenseData });

    } catch (error) {
      console.error('Error quick adding expense:', error);
      toast({
        title: "Error",
        description: "Failed to add expense. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleCancel = () => {
    setParsedData(null);
    clearParsedDraft();
    setInput("");
    clearInputDraft();
  };

  const handleSuccessComplete = () => {
    setShowSuccessCard(false);
    setSavedExpense(null);
    setSavedIncome(null);
    setParsedData(null);
    clearParsedDraft();
    setInput("");
    clearInputDraft();
    setSaveAsTemplate(false);
    onExpenseAdded();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !parsedData) {
      handleParse();
    }
  };

  return (
    <>
      <SaveFeedback show={showSaveFeedback} />
      <div className="space-y-3">
        {showSuccessCard && (savedExpense || savedIncome) ? (
          <SuccessCard
            amount={savedExpense?.amount || savedIncome?.amount}
            currency={savedExpense?.currency || savedIncome?.currency}
            category={savedExpense?.category || savedIncome?.source}
            merchant={savedExpense?.merchant || savedIncome?.source}
            note={savedExpense?.note}
            streakDays={streakDays}
            calmMode={calmMode}
            motivationStyle={settings.style}
            onComplete={handleSuccessComplete}
          />
        ) : !parsedData ? (
        <div className="space-y-3">
          {/* Voice Recording Mode - replaces ENTIRE input section */}
          {isVoiceRecording ? (
            <VoiceButton 
              onTranscript={(text) => setInput(text)} 
              onRecordingStart={() => setIsVoiceRecording(true)}
              onRecordingEnd={() => setIsVoiceRecording(false)}
            />
          ) : (
            <>
              {/* Quick Actions - hidden during recording */}
              {user && <QuickActions userId={user.id} onQuickAdd={handleQuickAdd} />}

              {/* Input with Voice Button */}
              <div className="relative flex items-center gap-2">
                <Input
                  ref={inputRef}
                  type="text"
                  inputMode="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder={t('expense.inputPlaceholder')}
                  className="flex-1 h-12 text-base pr-12"
                />
                {!input && (
                  <div className="absolute right-2 top-1/2 -translate-y-1/2">
                    <VoiceButton 
                      onTranscript={(text) => setInput(text)} 
                      onRecordingStart={() => setIsVoiceRecording(true)}
                      onRecordingEnd={() => setIsVoiceRecording(false)}
                    />
                  </div>
                )}
              </div>

              <Button onClick={handleParse} className="w-full h-12 text-base" disabled={!input.trim()}>
                {t('expense.addButton')}
              </Button>
            </>
          )}
        </div>
        ) : (
          <div className="space-y-4 p-6 bg-card border-2 border-primary rounded-lg animate-scale-in">
            {parsedData.is_clarifying ? (
              <div className="space-y-3">
                <p className="text-lg font-medium">{parsedData.question}</p>
                <Input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type your answer here..."
                  className="text-lg min-h-touch border-2 focus:border-primary"
                  autoFocus
                />
                {parsedData.suggestions && (
                  <div className="grid grid-cols-2 gap-2">
                    {parsedData.suggestions.map((suggestion) => (
                      <Button
                        key={suggestion}
                        variant="outline"
                        onClick={() => {
                          const newInput = `${input} ${suggestion}`;
                          setInput(newInput);
                          setParsedData(null);
                          setTimeout(() => {
                            handleParse();
                          }, 100);
                        }}
                        className="min-h-touch"
                      >
                        {suggestion}
                      </Button>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <Button 
                    onClick={() => {
                      setParsedData(null);
                      setTimeout(() => handleParse(), 100);
                    }} 
                    className="flex-1 min-h-touch"
                  >
                    {t('expense.continue')}
                  </Button>
                  <Button onClick={handleCancel} variant="outline" className="min-h-touch">
                    {t('expense.cancel')}
                  </Button>
                </div>
              </div>
            ) : (
              /* Unified Confirmation UI with Type Toggle */
              <div className="space-y-4">
                {/* Type Toggle - Always show when we have parsed data */}
                <TypeToggle
                  isIncome={isIncome}
                  onToggle={(newIsIncome) => {
                    if (parsedData) {
                      setParsedData({
                        ...parsedData,
                        type: newIsIncome ? 'income' : 'expense',
                        // When switching to income, use merchant as source if not set
                        income_source: newIsIncome 
                          ? (parsedData.income_source || parsedData.merchant) 
                          : undefined,
                      });
                    }
                  }}
                  disabled={isSaving}
                />

                {isIncome ? (
                  /* Income Details */
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground">{t('expense.amount') || 'Amount'}</p>
                      <p className="text-2xl font-bold text-success">
                        {parsedData.currency} {parsedData.amount.toFixed(2)}
                      </p>
                    </div>
                    
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground">{t('income.source') || 'Source'}</p>
                      <p className="text-lg font-semibold">
                        {parsedData.income_source || parsedData.merchant || 'Payment'}
                      </p>
                    </div>
                  </div>
                ) : (
                  /* Expense Details */
                  <ConfirmRow
                    amount={parsedData.amount}
                    currency={parsedData.currency}
                    merchant={parsedData.merchant}
                    date={parsedData.date}
                    category={parsedData.category}
                    categories={COMMON_CATEGORIES}
                    saveAsTemplate={saveAsTemplate}
                    onCategoryChange={(cat) => setParsedData({ ...parsedData, category: cat })}
                    onDateChange={(date) => setParsedData({ ...parsedData, date })}
                    onSaveAsTemplateChange={setSaveAsTemplate}
                  />
                )}

                {/* Action Buttons */}
                <div className="flex gap-2">
                  <Button
                    onClick={isIncome ? handleSaveIncome : handleSave}
                    disabled={isSaving}
                    className={cn(
                      "flex-1 min-h-touch",
                      isIncome 
                        ? "bg-success hover:bg-success/90 text-success-foreground"
                        : "bg-success hover:bg-success/90 text-success-foreground"
                    )}
                    size="lg"
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        {t('expense.saving') || 'Saving...'}
                      </>
                    ) : isIncome ? (
                      t('income.logIncome') || 'Log Income'
                    ) : (
                      t('expense.saveExpense') || 'Save Expense'
                    )}
                  </Button>
                  <Button
                    onClick={handleCancel}
                    variant="outline"
                    className="min-h-touch px-6"
                    size="lg"
                    disabled={isSaving}
                  >
                    {t('common.cancel') || 'Cancel'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};
